<template>
    <group-details :groupId="props.groupId">
        <form-wrapper
            :sections="[
                { id: 'group_id', label: 'ID' },
                { id: 'name', label: 'Name' },
                { id: 'time_zone', label: 'Time-zone' },
                { id: 'orgUnit', label: 'Organizational Unit' },
                { id: 'attributes', label: 'Attributes' },
            ]"
            :hideNav="Boolean(props.groupId)"
        >
            <group-form-external-id id="group_id"
                v-model="group.group_id"
                :locked="!!props.groupId || Boolean(queryParams.setup_group_id)"
                :disabled="!!props.groupId || Boolean(queryParams.setup_group_id)"
                :requiredLabel="!props.groupId"
                :error="displayFirstError(validation.group_id.$errors)">
            </group-form-external-id>

            <group-form-name id="name"
                v-model="group.name"
                :error="displayFirstError(validation.name.$errors)">
            </group-form-name>

            <group-form-time-zone id="time_zone"
                v-model="group.time_zone"
                :error="displayFirstError(validation.time_zone.$errors)">
            </group-form-time-zone>

            <group-form-org-unit id="orgUnit" v-model="group.org_unit"></group-form-org-unit>

            <group-form-phone-numbers v-if="props.groupId && !groupsUseCorporateNumbers"
                v-model="phoneNumbers">
            </group-form-phone-numbers>

            <group-form-custom-attributes id="attributes"
                v-model="customAttributes">
            </group-form-custom-attributes>

            <vx-sticky-footer>
                <vx-button :href="`/settings/teams/${teamId}#/groups`"
                    v-if="onCorporate()"
                    class="w-20"
                    size="lg"
                    color="muted">
                    Cancel
                </vx-button>
                <vx-button @click="submit()"
                    :disabled="!onCorporate() && !['admin'].includes(roleOnCurrentGroup)"
                    data-test="submit-button"
                    class="grow"
                    size="lg"
                    color="primary"
                    :loading="processing">
                    {{
                        props.groupId
                            ? 'Update'
                            : 'Create'
                    }} Group
                </vx-button>
            </vx-sticky-footer>

        </form-wrapper>
    </group-details>
</template>

<script setup>
import { VxButton, VxStickyFooter } from '@voxie/frontend-components';
import { faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { ref } from 'vue';
import { required, maxLength } from '@vuelidate/validators';
import { alphanumericDashUnderscore, groupAndOrgUnitName, displayFirstError } from '~/utils/validation';
import GroupDetails from '~/components/settings/teams/groups/form/GroupDetails.vue';
import GroupFormExternalId from '~/components/settings/teams/groups/form/GroupFormExternalId.vue';
import GroupFormName from '~/components/settings/teams/groups/form/GroupFormName.vue';
import GroupFormTimeZone from '~/components/settings/teams/groups/form/GroupFormTimeZone.vue';
import GroupFormCustomAttributes from './GroupFormCustomAttributes.vue';
import { groupsClient, phoneNumbersClient } from '~/services';
import GroupFormPhoneNumbers from './GroupFormPhoneNumbers.vue';
import { useGroupDetails } from './useGroupDetails';
import { useUrlSearchParams } from '@vueuse/core';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import FormWrapper from '~/components/general/FormWrapper.vue';
import GroupFormOrgUnit from '~/components/settings/teams/groups/form/GroupFormOrgUnit.vue';
import orgUnitGroupsClient from '~/services/orgUnitGroupsClient';
import { useFormSubmission } from '~/composables/useFormSubmission.js';
import { catchRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();
const teamId = Spark.state.currentTeam.id;
const groupsUseCorporateNumbers = Spark.state.currentTeam.groups_use_corporate_numbers;

const props = defineProps({
    groupId: {
        type: String,
        required: false,
    },
});

const { group, onMountedGroup, defaultGroup } = useGroupDetails();
const { onCorporate, roleOnCurrentGroup } = useCurrentGroup();

const queryParams = useUrlSearchParams();

const loadingPhoneNumbers = ref(false);
const loadingCustomAttributes = ref(false);

const customAttributes = ref([]);
const originalCustomAttributes = ref([]);
const phoneNumbers = ref([]);
const previousOrgUnit = ref();

const newGroupId = ref();

onMountedGroup(() => {
    if (!props.groupId && queryParams.setup_group_id) {
        group.value.group_id = queryParams.setup_group_id;
    }

    if (props.groupId) {
        previousOrgUnit.value = group.value.org_unit;
        getGroupCustomAttributes();

        if (!groupsUseCorporateNumbers) {
            getGroupPhoneNumbers();
        }

    }
});

const save = async () => {
    if (!props.groupId) {
        const response = await createGroup();
        newGroupId.value = response.data.group_id;
    } else {
        await groupsClient.updateGroup({ teamId }, props.groupId, {
            name: group.value.name,
            time_zone: group.value.time_zone
        });
        await syncGroupCustomAttributes();

        // update org unit
        if (group.value.org_unit?.id !== previousOrgUnit.value?.id) {
            await removeFromOrgUnit(props.groupId);
            await addToOrgUnit(props.groupId);
        }

        previousOrgUnit.value = group.value.org_unit ? { ...group.value.org_unit } : null;
        originalCustomAttributes.value = customAttributes.value.map(attribute => ({...attribute}));
        queryParams.setup_group_id = undefined;
    }
};

const { submit, processing, validation } = useFormSubmission({
    data: group,
    validation: {
        group_id: {
            required,
            maxLength: maxLength(40),
            format: alphanumericDashUnderscore,
        },
        name: {
            required,
            maxLength: maxLength(80),
            format: groupAndOrgUnitName,
        },
        time_zone: {},
    },
    preserveScroll: () => Boolean(props.groupId),
    submit: save,
    reset: () => {
        if (!props.groupId) {
            group.value = defaultGroup();
        }
    },
    notification: () =>
        props.groupId
            ? {
                message: 'Group updated',
                action: {
                    icon: faSquareArrowUpLeft,
                    label: 'Back to Groups',
                    href: `/settings/teams/${teamId}#/groups`,
                },
            }
            : {
                message: 'Group created',
                redirect: `/settings/teams/${teamId}/groups/${newGroupId.value}/users`,
            },
});

const getGroupCustomAttributes = async () => {
    try {
        loadingCustomAttributes.value = true;
        const response = await groupsClient.getGroupCustomAttributes({ teamId }, props.groupId);
        customAttributes.value = response.data.data.map(attribute => ({...attribute}));
        originalCustomAttributes.value = response.data.data.map(attribute => ({...attribute}));
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        loadingCustomAttributes.value = false;
    }
};

const getGroupPhoneNumbers = async () => {
    try {
        loadingPhoneNumbers.value = true;
        const response = await phoneNumbersClient.listPhoneNumbers(teamId, { group_id: props.groupId });
        phoneNumbers.value = response.data.data;
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        loadingPhoneNumbers.value = false;
    }
};

const syncGroupCustomAttributes = () => {
    return Promise.all([deleteCustomAttributes(), updateCustomAttributes(), createCustomAttributes()]);
};

const deleteCustomAttributes = () => {
    const attributesToDelete = originalCustomAttributes.value.filter(
        attribute => !customAttributes.value.map(a => a.key).includes(attribute.key)
    );

    return Promise.all(attributesToDelete.map((customAttribute) => {
        return groupsClient.deleteGroupCustomAttribute({ teamId }, props.groupId, customAttribute.key);
    }));
};

const updateCustomAttributes = () => {
    const attributesToUpdate = customAttributes.value.filter(
        attribute => {
            const value = originalCustomAttributes.value.find(a => a.key === attribute.key)?.value;
            return undefined !== value && value !== attribute.value;
        }
    );

    return Promise.all(attributesToUpdate.map((customAttribute) => {
        return groupsClient.updateGroupCustomAttribute({ teamId }, props.groupId, customAttribute.key, { value: customAttribute.value });
    }));
};

const createCustomAttributes = () => {
    const attributesToCreate = customAttributes.value.filter(
        attribute => !originalCustomAttributes.value.map(a => a.key).includes(attribute.key)
    );

    return Promise.all(attributesToCreate.map((customAttribute) => {
        return groupsClient.createGroupCustomAttribute({ teamId }, props.groupId, customAttribute);
    }));
};

const createGroup = () => {
    const groupData = {
        ...group.value,
        custom_attributes: customAttributes.value,
    };

    if (group.value.org_unit?.id) {
        return orgUnitGroupsClient.createOrgUnitGroup(teamId, group.value.org_unit.id, groupData);
    }

    return groupsClient.createGroup({ teamId }, groupData);
};

const addToOrgUnit = (groupId) => {
    if (!group.value.org_unit?.id) {
        return;
    }

    return orgUnitGroupsClient.addOrgUnitGroup(teamId, group.value.org_unit.id, groupId);
};

const removeFromOrgUnit = (groupId) => {
    if (!previousOrgUnit.value?.id) {
        return;
    }

    return orgUnitGroupsClient.removeOrgUnitGroup(teamId, previousOrgUnit.value.id, groupId);
};

</script>
